import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Flame, Play, Pause, UserPlus, UserCheck, Users, Music, Eye, Heart } from 'lucide-react';
import { usePT } from '../utils/promoteTranslations';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import VerifiedBadge from './VerifiedBadge';

/**
 * Promoted profile card injected into the "vocaux" feed.
 * Displays: avatar (with story ring if active), name, description,
 * audio intro player, follower stats, and a follow CTA.
 */
const PromotedProfileCard = ({ promotedUser, onFollowed }) => {
  const t = usePT();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(promotedUser?.followers_count || 0);

  useEffect(() => {
    // Are we already following this user?
    if (!currentUser || !promotedUser) return;
    (async () => {
      try {
        const res = await axios.get(`/api/users/${promotedUser.id}`);
        setFollowing((res.data?.followers || []).includes(currentUser.id));
      } catch (_) { /* ignore */ }
    })();
  }, [promotedUser?.id, currentUser?.id]);

  if (!promotedUser) return null;

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) { navigate('/auth'); return; }
    try {
      const res = await axios.post(`/api/users/${promotedUser.id}/follow`);
      setFollowing(!!res.data.following);
      setFollowerCount((c) => c + (res.data.following ? 1 : -1));
      if (res.data.following) onFollowed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div
      className="relative bg-gradient-to-br from-orange-500/10 via-magenta-500/10 to-violet-500/10 border border-orange-500/30 rounded-3xl overflow-hidden shadow-2xl shadow-orange-500/10"
      data-testid="promoted-profile-card"
    >
      {/* Promo label */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-orange-500 to-magenta-600 rounded-full text-xs font-bold uppercase tracking-wide">
        <Flame className="w-3 h-3" /> {t('promote.promoLabel')}
      </div>

      <div className="p-6">
        {/* Avatar + name row */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate(`/profile/${promotedUser.id}`)}
            className={`relative rounded-full ${promotedUser.has_active_story ? 'p-[3px] bg-gradient-to-tr from-orange-400 via-magenta-500 to-violet-500' : ''}`}
            data-testid="promoted-avatar-btn"
          >
            {promotedUser.profile_image_url ? (
              <img
                src={getMediaUrl(promotedUser.profile_image_url)}
                alt={promotedUser.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-900"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 flex items-center justify-center border-2 border-gray-900">
                <span className="text-2xl font-bold">{(promotedUser.name || '?')[0]}</span>
              </div>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <button onClick={() => navigate(`/profile/${promotedUser.id}`)} className="flex items-center gap-1.5">
              <h3 className="font-bold text-lg truncate">{promotedUser.name}</h3>
              {promotedUser.is_verified && <VerifiedBadge />}
            </button>
            {promotedUser.username && (
              <p className="text-sm text-gray-400">@{promotedUser.username}</p>
            )}
          </div>
        </div>

        {/* Description */}
        {(promotedUser.bio || promotedUser.description) && (
          <p className="text-sm text-gray-300 mb-4 line-clamp-3">
            {promotedUser.bio || promotedUser.description}
          </p>
        )}

        {/* Audio intro */}
        {promotedUser.audio_intro_url && (
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-3 mb-4">
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-magenta-600 flex items-center justify-center shadow-lg"
              data-testid="promoted-audio-play-btn"
            >
              {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-1 h-6">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} className="flex-1 rounded-full bg-magenta-400/40" style={{ height: `${30 + Math.sin(i * 0.6) * 15 + (i % 3) * 8}%` }} />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Audio · Introduction</p>
            </div>
            <audio ref={audioRef} src={getMediaUrl(promotedUser.audio_intro_url)} onEnded={() => setPlaying(false)} />
          </div>
        )}

        {/* Stats — compact */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
          <StatBox label={t('promote.followers')} value={followerCount} />
          <StatBox label={t('promote.following')} value={promotedUser.following_count || 0} />
          <StatBox label={t('promote.posts')} value={promotedUser.posts_count || 0} />
          {(promotedUser.total_likes != null) && <StatBox label={t('promote.likes')} value={promotedUser.total_likes} />}
          {(promotedUser.total_views != null) && <StatBox label={t('promote.views')} value={promotedUser.total_views} />}
        </div>

        {/* Follow CTA */}
        <button
          onClick={handleFollow}
          className={`w-full px-4 py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 ${
            following ? 'bg-white/10 hover:bg-white/20' : 'bg-gradient-to-r from-orange-500 to-magenta-600 hover:from-orange-600 hover:to-magenta-700'
          }`}
          data-testid="promoted-follow-btn"
        >
          {following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          {following ? t('promote.followingBtn') : t('promote.followBtn')}
        </button>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }) => (
  <div className="p-2 rounded-xl bg-white/5 border border-white/10">
    <p className="font-bold text-white">{value ?? 0}</p>
    <p className="text-gray-400">{label}</p>
  </div>
);

export default PromotedProfileCard;
